import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Label
} from "reactstrap";
import { APIClient } from "../../helpers/api_helper";
import { GENERAL_DAILY_STATS_URL, GENERAL_WEEKLY_STATS_URL, ALL_GAME_STATS_URL } from "../../helpers/url_helper";
import Chart from "react-apexcharts";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { toast } from "react-toastify";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [weeklyLoading, setWeeklyLoading] = useState(false);

  // Local-safe date formatter (YYYY-MM-DD)
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [statsData, setStatsData] = useState({
    summary: { totalBets: 0, totalPayout: 0, adminProfit: 0, totalRounds: 0 },
    data: []
  });

  const [allGamesStats, setAllGamesStats] = useState({
    totalBetsGross: 0,
    totalPayouts: 0,
    totalNetProfit: 0,
    totalGameRounds: 0
  });

  const [weeklyData, setWeeklyData] = useState({
    summary: { totalBets: 0, totalPayout: 0, adminProfit: 0 },
    days: []
  });

  // Calculate current week's Sunday in local time
  const getDefaultStartDate = () => {
    const today = new Date();
    const day = today.getDay(); // 0 is Sunday
    const sunday = new Date(today);
    sunday.setDate(today.getDate() - day);
    return formatLocalDate(sunday);
  };

  const [selectedStartDate, setSelectedStartDate] = useState(null);

  const api = new APIClient();

  // Fetch Daily Stats
  const fetchDailyStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(GENERAL_DAILY_STATS_URL);
      if (response && response.data) {
        setStatsData(response.data);
      }
    } catch (error) {
      toast.error("Error fetching daily statistics: " + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch Weekly Stats
  const fetchWeeklyStats = useCallback(async (date) => {
    try {
      setWeeklyLoading(true);
      const params = date ? { startDate: date } : {};
      const response = await api.get(GENERAL_WEEKLY_STATS_URL, params);
      if (response && response.data) {
        setWeeklyData(response.data);
        // Sync the selector with the API's start date
        if (response.data.summary?.startDate) {
          setSelectedStartDate(response.data.summary.startDate);
        }
      }
    } catch (error) {
      toast.error("Error fetching weekly statistics: " + error.message);
    } finally {
      setWeeklyLoading(false);
    }
  }, []);

  const fetchAllGamesStats = useCallback(async () => {
    try {
      const response = await api.get(ALL_GAME_STATS_URL);
      if (response && response.data) {
        setAllGamesStats(response.data);
      }
    } catch (error) {
      console.error("Error fetching all games statistics: ", error);
    }
  }, []);

  useEffect(() => {
    fetchDailyStats();
    fetchAllGamesStats();
  }, [fetchDailyStats, fetchAllGamesStats]);

  // Initial load
  useEffect(() => {
    fetchWeeklyStats();
  }, [fetchWeeklyStats]);

  const handleDateChange = (dates) => {
    if (dates.length > 0) {
      const selected = formatLocalDate(dates[0]);
      console.log(`📡 Fetching stats for selected date: ${selected}`);
      fetchWeeklyStats(selected);
    }
  };

  document.title = "Main Dashboard | " + (process.env.REACT_APP_SITE_TITLE || "Admin Panel");

  // Daily Chart Configuration
  const dailyChartOptions = {
    chart: {
      height: 380,
      type: 'area', // Area chart
      toolbar: { show: false },
    },
    colors: ['#0ab39c'],
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 3 },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.45,
        opacityTo: 0.05,
        stops: [20, 100, 100, 100]
      }
    },
    title: {
      text: 'Today\'s Profit by Interval',
      align: 'left',
      style: { fontWeight: 700, fontSize: '16px', color: '#495057' }
    },
    xaxis: {
      categories: statsData.data.map(item => {
        if (!item.interval) return '';
        return item.interval.split(' - ').map(t => {
          const [time, modifier] = t.split(' ');
          let [hours, minutes] = time.split(':');
          if (hours === '12') hours = '00';
          if (modifier === 'PM') hours = String(parseInt(hours, 10) + 12);
          return `${hours.padStart(2, '0')}:${minutes}`;
        }).join(' - ');
      }),
      title: { text: 'Time Interval' }
    },
    yaxis: {
      title: { text: 'Profit (Points)' },
      labels: { formatter: (val) => val.toLocaleString() }
    },
    legend: { position: 'top', horizontalAlign: 'right', offsetY: -30 },
    markers: { size: 4, hover: { size: 6 } }
  };

  const dailyChartSeries = [
    { name: "Admin Profit", data: statsData.data.map(item => item.adminProfit) }
  ];

  // Weekly Chart Configuration
  const weeklyChartOptions = {
    chart: {
      height: 380,
      type: 'area', // Area for a different premium look
      toolbar: { show: false }
    },
    colors: ['#f06548', '#0ab39c'],
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 3 },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.45,
        opacityTo: 0.05,
        stops: [20, 100, 100, 100]
      }
    },
    title: {
      text: `Weekly Revenue & Profit (${weeklyData.summary?.startDate} to ${weeklyData.summary?.endDate})`,
      align: 'left',
      style: { fontWeight: 700, fontSize: '16px', color: '#495057' }
    },
    xaxis: {
      categories: weeklyData.days.map(item => `${item.dayName} (${item.date.split('-').slice(1).join('/')})`),
      title: { text: 'Day' }
    },
    yaxis: {
      title: { text: 'Points' },
      labels: { formatter: (val) => val.toLocaleString() }
    },
    legend: { position: 'top', horizontalAlign: 'right', offsetY: -30 },
    markers: { size: 4, hover: { size: 6 } }
  };

  const weeklyChartSeries = [
    { name: "Total Payout", data: weeklyData.days.map(item => item.totalPayout) },
    { name: "Admin Profit", data: weeklyData.days.map(item => item.adminProfit) }
  ];

  const { totalBetsGross, totalPayouts, totalNetProfit, totalGameRounds } = allGamesStats;

  const SummaryCard = ({ title, value, icon, color }) => (
    <Card className="card-animate overflow-hidden card-glass mb-4 shadow-sm border-0">
      <CardBody>
        <div className="d-flex align-items-center">
          <div className="flex-grow-1 overflow-hidden">
            <p className="text-uppercase fw-bold text-muted text-truncate mb-0 fs-11">
              {title}
            </p>
          </div>
        </div>
        <div className="d-flex align-items-end justify-content-between">
          <div>
            <h4 className="fs-22 fw-black ff-secondary mb-3">
              <span className="counter-value">
                {typeof value === 'number' ? value.toLocaleString() : value}
              </span>
            </h4>
          </div>
          <div className="avatar-md flex-shrink-0">
            <span className={`avatar-title bg-${color}-subtle rounded-3 fs-1`}>
              <i className={`${icon} text-${color} opacity-75`}></i>
            </span>
          </div>
        </div>
      </CardBody>
    </Card>
  );

  return (
    <React.Fragment>
      <div className="page-content mb-4">
        <Container fluid>
          <BreadCrumb title="Admin Overview" pageTitle="Dashboard" />
          <div className="mt-5">
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                  <Spinner color="primary" size="lg" className="mb-3" />
                  <p className="text-muted fw-medium">Loading Dashboard Data...</p>
                </div>
              </div>
            ) : (
              <>
                <Row>
                  <Col xl={3} md={6}>
                    <SummaryCard title="Total Bets (Gross)" value={totalBetsGross} icon="ri-coins-line" color="primary" />
                  </Col>
                  <Col xl={3} md={6}>
                    <SummaryCard title="Total Payouts" value={totalPayouts} icon="ri-hand-coin-line" color="danger" />
                  </Col>
                  <Col xl={3} md={6}>
                    <SummaryCard title="Total Net Profit" value={totalNetProfit} icon="ri-money-dollar-circle-line" color="success" />
                  </Col>
                  <Col xl={3} md={6}>
                    <SummaryCard title="Total Game Rounds" value={totalGameRounds} icon="ri-restart-line" color="info" />
                  </Col>
                </Row>

                {/* Weekly Graph Section */}
                <Row className="mt-2">
                  <Col xl={12}>
                    <Card className="shadow-sm border-0">
                      <CardHeader className="d-flex align-items-center bg-white border-bottom p-3">
                        <h4 className="card-title mb-0 flex-grow-1 fw-bold text-dark">
                          <i className="ri-calendar-check-line me-2 text-info"></i>
                          Weekly Performance Analysis
                        </h4>
                        <div className="flex-shrink-0 d-flex align-items-center gap-3">
                          <div className="d-flex align-items-center gap-2">
                            <Label className="mb-0 text-muted fw-semibold fs-12">Start Sunday:</Label>
                            <Flatpickr
                              className="form-control form-control-sm bg-light border-0 py-1 text-center"
                              style={{ width: '110px', fontSize: '12px', height: '28px' }}
                              value={selectedStartDate || ""}
                              options={{
                                dateFormat: "Y-m-d",
                                onChange: handleDateChange
                              }}
                            />
                          </div>
                          {weeklyLoading && <Spinner size="sm" color="info" className="me-2" />}
                          <span className="badge bg-info-subtle text-info rounded-pill px-3 py-2 fs-11 fw-bold">
                            {weeklyData.summary?.startDate} to {weeklyData.summary?.endDate}
                          </span>
                        </div>
                      </CardHeader>
                      <CardBody>
                        <Chart
                          options={weeklyChartOptions}
                          series={weeklyChartSeries}
                          type="area"
                          height="380"
                          className="apex-charts"
                        />
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Daily Graph Section */}
                <Row className="mt-2">
                  <Col xl={12}>
                    <Card className="shadow-sm border-0">
                      <CardHeader className="d-flex align-items-center bg-white border-bottom p-3">
                        <h4 className="card-title mb-0 flex-grow-1 fw-bold text-dark">
                          <i className="ri-bar-chart-2-line me-2 text-success"></i>
                          Daily Profitability Overview
                        </h4>
                      </CardHeader>
                      <CardBody>
                        <Chart
                          options={dailyChartOptions}
                          series={dailyChartSeries}
                          type="area"
                          height="380"
                          className="apex-charts"
                        />
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </>
            )}
          </div>
        </Container>
      </div>
      <style>{`
        .card-glass {
            background: rgba(255, 255, 255, 0.9) !important;
            backdrop-filter: blur(10px);
            transition: all 0.3s ease-in-out;
        }
        .card-glass:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-premium {
            background: linear-gradient(135deg, #405189 0%, #0d9582 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        .fw-black {
            font-weight: 800;
        }
        .fs-11 { font-size: 11px; }
        .fs-12 { font-size: 12px; }
        .fs-13 { font-size: 13px; }
        .fs-10 { font-size: 10px; }
        
        /* Premium Badge Styling */
        .badge-soft-info {
            background-color: rgba(10, 179, 156, 0.1) !important;
            color: #0d9582 !important;
            border: 1px solid rgba(10, 179, 156, 0.2);
        }

        /* Flatpickr Customization */
        .flatpickr-day.selected, 
        .flatpickr-day.selected:focus, 
        .flatpickr-day.selected:hover {
            background: #0d9582 !important;
            border-color: #0d9582 !important;
            color: #fff !important;
            box-shadow: 0 4px 10px rgba(13, 149, 130, 0.3);
        }
        .flatpickr-months .flatpickr-month {
            background: #0d9582 !important;
            color: #fff !important;
            fill: #fff !important;
            border-radius: 5px 5px 0 0;
        }
        .flatpickr-current-month .flatpickr-monthDropdown-months,
        .flatpickr-current-month input.cur-year {
            font-weight: bold !important;
            color: #fff !important;
            font-size: 14px;
        }
        .flatpickr-months .flatpickr-prev-month, 
        .flatpickr-months .flatpickr-next-month {
            fill: #fff !important;
            padding: 8px;
            transition: all 0.2s ease;
        }
        .flatpickr-months .flatpickr-prev-month:hover, 
        .flatpickr-months .flatpickr-next-month:hover {
            background: rgba(255,255,255,0.1);
        }
        .flatpickr-weekdays {
            background: #0d9582 !important;
            height: 28px !important;
        }
        span.flatpickr-weekday {
            background: #0d9582 !important;
            color: rgba(255,255,255,0.8) !important;
            font-size: 11px;
            font-weight: 600;
        }
      `}</style>
    </React.Fragment>
  );
};

export default Dashboard;
